/**
 * FleetTable — one row per workflow, the "supervise hundreds by exception" surface
 * (UI-SPEC §1). Workflows needing attention sort to the top; healthy ones recede.
 * Read-only in Phase 2 (attainment/trend bars + alerts arrive with the eval
 * layer); columns degrade to "—" until that data exists. Composes <DataTable>
 * + <StatusBadge> + <StatusDot>.
 */

import * as React from "react";
import { RouterLink as Link } from "../../router";
import { DataTable, type DataTableColumn } from "../data-table";
import { StatusBadge, IntentBadge } from "../status-badge";
import { StatusDot } from "../status-dot";
import { Icon } from "../../icon";
import { formatUsd, formatRelativeTime } from "../../format";
import { loopSummary } from "./loop-format";
import type { LoopStatus } from "../../tokens";

export interface FleetRow {
  id: number;
  slug: string;
  title: string;
  /** Plain-English summary, rendered as a subtitle under the name. */
  description?: string | null;
  /** Ordered raw step names (unused in the table; powers the step count). */
  chain?: string[];
  domain: string | null;
  status: LoopStatus;
  active_version: number | null;
  step_count: number;
  /** Run count / spend / tokens within the selected window (lifetime by default). */
  runs: number;
  cost: number;
  tokens: number;
  /** Most recent run (lifetime); null/undefined = never run. */
  last_run_at?: string | null;
  /**
   * True when an enabled event subscription fires this workflow (Data Design
   * PRD §6.7) — renders a lightning badge next to the name.
   */
  trigger_enabled?: boolean;
  /** When the trigger last fired — rendered via formatRelativeTime. */
  trigger_last_fired_at?: string | null;
}

/** Compact token count: 1.2M / 3.4k / raw. Shared with `SectionLoopsPanel`. */
export function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/**
 * The shared fleet column set, factored out so the section-scoped variant
 * (`SectionLoopsPanel`) composes them instead of re-declaring them. A row only
 * needs the `FleetRow` fields, so any superset works. `statusOf` reads the
 * status to render (the section panel injects an optimistic override accessor;
 * FleetTable defaults to `r.status`). `includeDomain` keeps the domain column —
 * FleetTable shows it, the section panel (already scoped to one domain) drops it.
 */
export function buildFleetColumns<R extends FleetRow>(opts?: {
  statusOf?: (r: R) => LoopStatus;
  includeDomain?: boolean;
  /** Window label qualifying the run/cost/token headers, e.g. "all time" / "24h". */
  rangeLabel?: string;
}): DataTableColumn<R>[] {
  const statusOf = opts?.statusOf ?? ((r: R) => r.status);
  const includeDomain = opts?.includeDomain ?? true;
  const suffix = opts?.rangeLabel ? ` (${opts.rangeLabel})` : "";
  const columns: DataTableColumn<R>[] = [
    {
      key: "loop",
      header: "workflow",
      render: (r) => {
        const summary = loopSummary(r.description);
        return (
          <div className="flex items-start gap-2">
            <StatusDot domain="loop" status={statusOf(r)} className="mt-1.5 shrink-0" />
            <div className="flex min-w-0 flex-col">
              <span className="flex items-center gap-1.5">
                <Link
                  href={`/agents/workflows/${r.slug}`}
                  className="font-medium text-foreground hover:underline"
                >
                  {r.title}
                </Link>
                {r.trigger_enabled && (
                  <IntentBadge intent="info">
                    <Icon name="zap" size="xs" />
                    Triggered
                  </IntentBadge>
                )}
              </span>
              {r.trigger_enabled ? (
                <span className="line-clamp-1 type-subtext text-muted-foreground">
                  {r.trigger_last_fired_at
                    ? `Last fired ${formatRelativeTime(r.trigger_last_fired_at)}`
                    : "Armed — waiting for a matching event"}
                </span>
              ) : (
                summary && (
                  <span className="line-clamp-1 type-subtext text-muted-foreground">{summary}</span>
                )
              )}
            </div>
          </div>
        );
      },
    },
    ...(includeDomain
      ? [
          {
            key: "domain",
            header: "domain",
            cellClassName: "text-muted-foreground",
            render: (r: R) => r.domain ?? "—",
          } satisfies DataTableColumn<R>,
        ]
      : []),
    {
      key: "state",
      header: "state",
      align: "center",
      render: (r) => <StatusBadge domain="loop" status={statusOf(r)} />,
    },
    {
      key: "variant",
      header: "variant",
      align: "center",
      cellClassName: "tabular-nums",
      render: (r) =>
        r.active_version !== null ? (
          <Link href={`/agents/workflows/${r.slug}#variants`} className="text-foreground hover:underline">
            v{r.active_version}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { key: "steps", header: "steps", align: "center", cellClassName: "tabular-nums text-muted-foreground", render: (r) => String(r.step_count) },
    { key: "runs", header: `runs${suffix}`, align: "center", cellClassName: "tabular-nums", render: (r) => String(r.runs) },
    { key: "cost", header: `cost${suffix}`, cellClassName: "tabular-nums", render: (r) => formatUsd(r.cost) },
    { key: "tokens", header: `tokens${suffix}`, cellClassName: "tabular-nums text-muted-foreground", render: (r) => fmtTokens(r.tokens) },
    {
      key: "last_run",
      header: "last run",
      cellClassName: "tabular-nums text-muted-foreground",
      render: (r) => (r.last_run_at ? formatRelativeTime(r.last_run_at) : "never"),
    },
  ];
  return columns;
}

export function FleetTable({
  rows,
  rangeLabel,
  summary,
}: {
  rows: FleetRow[];
  /** Window label qualifying the run/cost/token headers (e.g. "all time"). */
  rangeLabel?: string;
  /** Caption band (e.g. a `<StatStrip variant="table">`) rendered flush above
   *  the column header, making the fleet KPIs the table's own caption. */
  summary?: React.ReactNode;
}) {
  const columns = buildFleetColumns<FleetRow>({ rangeLabel });
  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.id}
      empty="No workflows yet."
      summary={summary}
    />
  );
}
